import { useEffect, useState } from 'react';
import {
  ArrowUpRight,
  Check,
  Coffee,
  Home,
  Users,
  HeartHandshake,
  ChefHat,
  MapPin,
  Ticket,
  RefreshCw,
  UserRoundPlus,
  UserRoundX,
} from 'lucide-react';
import { text, type Locale, getLocation } from '../shared/content';
import type { Character, Coop, ScenePeer } from '../shared/types';
import { api } from './api';
const copy = {
  friends: text('Your people', '你的邻里好友', '你的鄰里好友', 'Tu gente'),
  nearby: text('Residents in this scene', '同场景的居民', '同場景的居民', 'Vecinos en esta escena'),
  alreadyFriend: text('Friend', '已是好友', '已是好友', 'Amigo'),
  invite: text('Say hello', '发个好友邀请', '送出好友邀請', 'Enviar invitación'),
  empty: text(
    'Good neighbors start with a hello. Add a resident from the city journal.',
    '从一个招呼开始认识好邻居。在城市动态中添加居民为好友。',
    '從一個招呼開始認識好鄰居。在城市動態中新增居民為好友。',
    'Todo empieza con un saludo. Añade vecinos desde el diario.',
  ),
  accept: text('Say hello back', '接受邀请', '接受邀請', 'Aceptar invitación'),
  decline: text('Decline', '拒绝', '拒絕', 'Rechazar'),
  pending: text('Invitation sent', '邀请已发出', '邀請已送出', 'Invitación enviada'),
  visit: text('Visit home', '看看对方的家', '看看對方的家', 'Visitar hogar'),
  meet: text('Meet in the neighborhood', '在街区会合', '在街區會合', 'Encontrarse en el barrio'),
  remove: text('Remove friend', '删除好友', '刪除好友', 'Eliminar amigo'),
  supper: text('The neighborhood supper', '街区晚餐计划', '街區晚餐計畫', 'La cena del barrio'),
  supperInfo: text(
    'Pick up groceries, prepare a little meal, and bring it to the park. Play together, even at different times.',
    '买好食材、做一份餐、送到公园。与朋友异步合作，不用同时在线。',
    '買好食材、做一份餐、送到公園。與朋友非同步合作，不用同時在線。',
    'Compra ingredientes, prepara algo y llévalo al parque. Colabora sin estar conectado a la vez.',
  ),
  npc: text('Jordan · NPC helper', 'Jordan · NPC 帮手', 'Jordan · NPC 幫手', 'Jordan · ayuda NPC'),
  start: text('Start a supper', '开始晚餐计划', '開始晚餐計畫', 'Organizar una cena'),
  gather: text('Pick up groceries', '领取食材', '領取食材', 'Recoger ingredientes'),
  prepare: text('Prepare your meal', '准备餐食', '準備餐點', 'Preparar la comida'),
  deliver: text('Bring it to the park', '送餐到公园', '送餐到公園', 'Llevar al parque'),
  go: text('Go to this step', '前往完成这一步', '前往完成這一步', 'Ir al siguiente paso'),
  step: text(
    'Complete this step · 3 energy',
    '完成这一步 · 3 精力',
    '完成這一步 · 3 精力',
    'Completar paso · 3 energía',
  ),
  switch: text('Continue with NPC help', '请 NPC 帮忙继续', '請 NPC 幫忙繼續', 'Continuar con un NPC'),
  switchInfo: text(
    'Either person can choose NPC assistance. Finish your own three steps for 25 vouchers; your friend’s real progress stays recorded and they can finish before expiry.',
    '双方都可选择 NPC 辅助。完成自己的三步即可领取 25 家居券；好友的真人进度会保留，到期前仍可继续完成。',
    '雙方都可選擇 NPC 輔助。完成自己的三步即可領取 25 家居券；好友的真人進度會保留，到期前仍可繼續完成。',
    'Cualquiera puede elegir ayuda NPC. Termina tus tres pasos para recibir 25 vales; el progreso real de tu amigo se conserva y puede terminar antes de que venza.',
  ),
  claim: text('Collect home vouchers', '领取家居券', '領取家居券', 'Recoger vales de hogar'),
  reward: text(
    'One reward per UTC day: 25 home vouchers with an NPC, 35 with a friend. Vouchers cannot be traded.',
    '每个 UTC 日可领一次奖励：NPC 合作 25 家居券，真人合作 35 家居券。家居券不可交易。',
    '每個 UTC 日可領一次獎勵：NPC 合作 25 家居券，真人合作 35 家居券。家居券不可交易。',
    'Una recompensa por día UTC: 25 vales con un NPC, 35 con un amigo. No transferibles.',
  ),
  waiting: text(
    'Your part is ready. Your friend can finish later.',
    '你已经完成了自己的部分，好友可以稍后接力。',
    '你已經完成了自己的部分，好友可以稍後接力。',
    'Tu parte está lista. Tu amigo puede terminar después.',
  ),
  complete: text('Supper delivered', '晚餐已送达', '晚餐已送達', 'Cena entregada'),
  expired: text(
    'This supper has ended. Start a new one anytime.',
    '这次计划已结束，可以随时开始下一次。',
    '這次計畫已結束，可以隨時開始下一次。',
    'Esta cena terminó. Puedes empezar otra cuando quieras.',
  ),
  claimed: text(
    'A little kindness, brought home.',
    '把一份善意，带回家。',
    '把一份善意，帶回家。',
    'Un poco de amabilidad, de vuelta a casa.',
  ),
  you: text('You', '你', '你', 'Tú'),
};
export type FriendRow = {
  id: string;
  status: 'pending' | 'accepted';
  incoming: boolean;
  character_id: string;
  name: string;
  city_id: string;
};
export function FriendsPanel({
  locale,
  revision,
  onAction,
  onVisit,
  onMeet,
  nearby = [],
}: {
  locale: Locale;
  revision: number;
  onAction: (type: string, payload: Record<string, unknown>) => Promise<unknown>;
  onVisit: (id: string) => void;
  onMeet: (id: string) => void;
  nearby?: ScenePeer[];
}) {
  const t = (k: keyof typeof copy) => copy[k][locale];
  const [friends, setFriends] = useState<FriendRow[]>([]);
  const [visible, setVisible] = useState(10),
    [requesting, setRequesting] = useState(false);
  useEffect(() => {
    api('/friends')
      .then((r) => setFriends(r.friends))
      .catch(() => {});
  }, [revision]);
  return (
    <>
      <h2>{t('friends')}</h2>
      {!!nearby.length && (
        <section className="scene-residents">
          <h3>
            {t('nearby')} · {nearby.length}
          </h3>
          {nearby.slice(0, visible).map((peer) => {
            const known = friends.find((f) => f.character_id === peer.characterId);
            return (
              <div className="friend-row" key={peer.characterId}>
                <span className="post-avatar">{peer.name[0]}</span>
                <strong>{peer.name}</strong>
                {known ? (
                  <small>{known.status === 'pending' ? t('pending') : t('alreadyFriend')}</small>
                ) : (
                  <button
                    className="text-button"
                    disabled={requesting}
                    onClick={async () => {
                      setRequesting(true);
                      try {
                        await onAction('friend.request', { targetId: peer.characterId });
                      } finally {
                        setRequesting(false);
                      }
                    }}
                  >
                    <UserRoundPlus size={13} />
                    {t('invite')}
                  </button>
                )}
              </div>
            );
          })}
          {nearby.length > visible && (
            <button className="text-button" onClick={() => setVisible((n) => n + 10)}>
              +10
            </button>
          )}
        </section>
      )}
      {friends.length ? (
        friends.map((f) => (
          <article className="friend-row" key={f.id}>
            <span className="post-avatar">{f.name[0]}</span>
            <div>
              <h3>{f.name}</h3>
              {f.status === 'pending' ? (
                f.incoming ? (
                  <div className="friend-actions">
                    <button
                      className="text-button"
                      onClick={() => onAction('friend.respond', { friendshipId: f.id, accept: true })}
                    >
                      <Check size={13} />
                      {t('accept')}
                    </button>
                    <button
                      className="text-button"
                      onClick={() => onAction('friend.respond', { friendshipId: f.id, accept: false })}
                    >
                      {t('decline')}
                    </button>
                  </div>
                ) : (
                  <small>{t('pending')}</small>
                )
              ) : (
                <div className="friend-actions">
                  <button className="text-button" onClick={() => onVisit(f.character_id)}>
                    <Home size={12} />
                    {t('visit')}
                  </button>
                  <button className="text-button" onClick={() => onMeet(f.character_id)}>
                    <MapPin size={12} />
                    {t('meet')}
                  </button>
                  <button
                    className="text-button muted"
                    onClick={() => onAction('friend.respond', { friendshipId: f.id, accept: false })}
                  >
                    <UserRoundX size={12} />
                    {t('remove')}
                  </button>
                </div>
              )}
            </div>
          </article>
        ))
      ) : (
        <div className="empty-card">
          <Users size={33} />
          <p>{t('empty')}</p>
        </div>
      )}
    </>
  );
}
export function CoopPanel({
  locale,
  character,
  jobs,
  busy,
  onAction,
  onGo,
}: {
  locale: Locale;
  character: Character;
  jobs: Coop[];
  busy: boolean;
  onAction: (type: string, payload: Record<string, unknown>) => Promise<unknown>;
  onGo: (id: string) => void;
}) {
  const t = (k: keyof typeof copy) => copy[k][locale];
  const [friends, setFriends] = useState<FriendRow[]>([]),
    [partner, setPartner] = useState('npc');
  useEffect(() => {
    api('/friends')
      .then((r) => setFriends(r.friends.filter((f: FriendRow) => f.status === 'accepted')))
      .catch(() => {});
  }, [character.revision]);
  const active = jobs.find(
    (j) => j.creatorId === character.id && !j.completedAt && Date.parse(j.expiresAt) > Date.now(),
  );
  return (
    <section className="coop-panel">
      <div className="coop-title">
        <HeartHandshake size={25} />
        <h3>{t('supper')}</h3>
      </div>
      <p className="muted small">{t('supperInfo')}</p>
      {!active && (
        <div className="coop-start">
          <select aria-label={t('supper')} value={partner} onChange={(e) => setPartner(e.target.value)}>
            <option value="npc">{t('npc')}</option>
            {friends.map((f) => (
              <option key={f.id} value={f.character_id}>
                {f.name}
              </option>
            ))}
          </select>
          <button
            className="button soft full"
            disabled={busy}
            onClick={() =>
              onAction('coop.create', {
                npcPartner: partner === 'npc',
                partnerId: partner === 'npc' ? undefined : partner,
              })
            }
          >
            <ChefHat size={15} />
            {t('start')}
          </button>
        </div>
      )}
      {jobs.slice(0, 4).map((j) => {
        const own = j.contributions[character.id] || [];
        const otherId = character.id === j.creatorId ? j.partnerId : j.creatorId;
        const otherName = character.id === j.creatorId ? j.partnerName : j.creatorName;
        const other = j.npcPartner ? t('npc') : otherName;
        const expired = Date.parse(j.expiresAt) <= Date.now() && (!j.completedAt || own.length < 3);
        const step = ['gather', 'prepare', 'deliver'][own.length] as
          'gather' | 'prepare' | 'deliver' | undefined;
        const placeId = own.length === 0 ? 'bodega' : own.length === 1 ? 'home' : 'park';
        const at = character.data.location === placeId && !character.data.travel;
        return (
          <article className="coop-job" key={j.id}>
            <header>
              <span className="post-avatar">{j.npcPartner ? <Coffee size={17} /> : <Users size={17} />}</span>
              <div>
                <strong>{other}</strong>
                <small>
                  {j.npcPartner
                    ? text('NPC assistance', 'NPC 辅助', 'NPC 輔助', 'Ayuda NPC')[locale]
                    : text('Player cooperation', '真人合作', '真人合作', 'Colaboración real')[locale]}
                </small>
              </div>
              <b>{own.length}/3</b>
            </header>
            {otherId && (
              <p className="muted small">
                {otherName} · {(j.contributions[otherId] || []).length}/3 ·{' '}
                {
                  text('Real player progress', '真人贡献记录', '真人貢獻記錄', 'Progreso del jugador real')[
                    locale
                  ]
                }
              </p>
            )}
            <div className="coop-steps">
              {(['gather', 'prepare', 'deliver'] as const).map((s, i) => (
                <span className={own.includes(s) ? 'done' : ''} key={s}>
                  {own.includes(s) ? <Check size={12} /> : <span>{i + 1}</span>}
                  {t(s)}
                </span>
              ))}
            </div>
            {expired ? (
              <p className="muted small">{t('expired')}</p>
            ) : j.claimed[character.id] ? (
              <p className="green small">{t('claimed')}</p>
            ) : j.completedAt && own.length === 3 ? (
              <button
                className="button primary full"
                disabled={busy}
                onClick={() => onAction('coop.claim', { coopId: j.id })}
              >
                <Ticket size={15} />
                {t('claim')} · {j.npcPartner ? 25 : 35}
              </button>
            ) : step ? (
              <button
                className="button soft full"
                disabled={busy || !!character.data.travel}
                onClick={() => (at ? onAction('coop.step', { coopId: j.id, step }) : onGo(placeId))}
              >
                <MapPin size={14} />
                {at ? t('step') : `${t('go')} · ${getLocation(placeId)!.name[locale]}`}
              </button>
            ) : (
              <p className="muted small">{t('waiting')}</p>
            )}
            {!j.npcPartner && !j.completedAt && !expired && (
              <>
                <button
                  className="text-button"
                  disabled={busy}
                  onClick={() => onAction('coop.switch-npc', { coopId: j.id })}
                >
                  <RefreshCw size={12} />
                  {t('switch')}
                </button>
                <p className="muted tiny">{t('switchInfo')}</p>
              </>
            )}
          </article>
        );
      })}
      <p className="muted tiny">{t('reward')}</p>
    </section>
  );
}
export const coopStepName = (locale: Locale, step: string) => copy[step as 'gather'][locale];
